/**
 * UploadPage.tsx — 2004va
 *
 * Changes from 1904va:
 * - Gated behind active shoot session — shows NewShootCard if no session
 * - Participant + event auto-populated from authStore / sessionStore
 * - Held status: anonymous uploads don't trigger push engine
 * - Contact capture → releaseHeldMedia → push engine runs for all held items
 * - Manual participant/event selects removed (inferred from session)
 */
import { useQuery } from '@tanstack/react-query'
import { Trash2, ChevronDown, ChevronUp, Camera, Clock, CheckSquare } from 'lucide-react'
import { useState, useRef } from 'react'
import { saveRightsProfile, triggerPushEngine, releaseHeldMedia, fetchParticipantActiveEvent } from '@/lib/supabase'
import { useUploadQueue } from '@/hooks/useUploadQueue'
import { UploadZone } from '@/components/upload/UploadZone'
import { QueueItemCard } from '@/components/upload/QueueItemCard'
import { NewShootCard } from '@/components/upload/NewShootCard'
import { Button, Badge } from '@/components/ui'
import { RightsPanel, DEFAULT_RIGHTS, type RightsConfig } from '@/components/upload/RightsPanel'
import { cn } from '@/components/ui/utils'
import { ContactCaptureSheet } from '@/components/ui/ContactCaptureSheet'
import { useAuthStore } from '@/stores/authStore'
import { useSessionStore, SHOOT_TYPE_LABELS } from '@/stores/sessionStore'
import type { Event, UploadQueueItem } from '@/types'

const CONTACT_PROMPT_DELAY_MS = 10 * 60 * 1000

export default function UploadPage() {
  const { participant, setParticipant } = useAuthStore()
  const { session, clearSession }       = useSessionStore()

  const [rights,        setRights]        = useState<RightsConfig>(DEFAULT_RIGHTS)
  const [showRights,    setShowRights]    = useState(false)
  const [pushedIds,     setPushedIds]     = useState<Set<string>>(new Set())
  const [showContact,   setShowContact]   = useState(false)
  const [holdsRights,   setHoldsRights]   = useState(false)
  const [noMinors,      setNoMinors]      = useState(false)
  const skipTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasPromptedRef = useRef(false)

  const { enqueue, cancel, retry, dismiss, items, activeCount, errorCount } = useUploadQueue()

  // Fetch active event for display — fallback if session doesn't have it
  const { data: activeEvent } = useQuery<Event | null>({
    queryKey: ['active-event', participant?.id],
    queryFn:  () => participant ? fetchParticipantActiveEvent(participant.id) : null,
    enabled:  !!participant && !session?.eventId,
    staleTime: 5 * 60 * 1000,
  })

  const event: Event | null = session
    ? { id: session.eventId ?? '', name: session.eventName ?? '', venue: session.venueName ?? undefined, created_at: '' }
    : activeEvent ?? null

  // ── Post-upload pipeline ────────────────────────────────────────────────────
  const handleDone = async (item: UploadQueueItem) => {
    if (!item.result?.mediaItemId || pushedIds.has(item.id)) return
    setPushedIds(prev => new Set([...prev, item.id]))

    try {
      await saveRightsProfile(item.result.mediaItemId, rights)

      // Only push if participant is identified (not anonymous / no contact)
      // Held items stay held — push runs in bulk when contact is captured
      const isHeld = participant?.is_anonymous && !participant?.contact_captured
      if (!isHeld) {
        await triggerPushEngine(item.result.mediaItemId)
      }
    } catch (e) {
      console.error('Post-upload pipeline error:', e)
    }

    // Soft contact prompt for anonymous users after first upload
    if (participant?.is_anonymous && !hasPromptedRef.current && !participant?.contact_captured) {
      hasPromptedRef.current = true
      setTimeout(() => setShowContact(true), 1500)
    }
  }

  const doneItems   = items.filter((i: UploadQueueItem) => i.status === 'done')
  const activeItems = items.filter((i: UploadQueueItem) => i.status !== 'done')

  doneItems.forEach((item: UploadQueueItem) => {
    if (!pushedIds.has(item.id)) handleDone(item)
  })

  // ── Contact captured → release held media ──────────────────────────────────
  async function handleContactDone() {
    setShowContact(false)
    if (!participant) return

    try {
      // Update auth store FIRST — any upload that fires during this async
      // block will see the participant as identified, not anonymous
      setParticipant({ ...participant, contact_captured: true, is_anonymous: false })

      const released = await releaseHeldMedia(participant.id)
      if (released > 0) {
        // Fetch newly-pending items and trigger push engine
        const { supabase } = await import('@/lib/supabase')
        const { data: heldItems } = await supabase
          .from('media_items')
          .select('id')
          .eq('participant_id', participant.id)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(Math.min(released, 50))  // cap — avoid runaway calls under volume

        if (heldItems) {
          await Promise.allSettled(
            heldItems.map((m: { id: string }) => triggerPushEngine(m.id))
          )
        }
      }
    } catch (e) {
      console.error('Release held media error:', e)
    }
  }

  // ── No active session → show New Shoot card ─────────────────────────────────
  if (!session) {
    return (
      <div className="space-y-6 animate-fade-in">
        <NewShootCard event={event} />
      </div>
    )
  }

  // ── Active session → show upload interface ──────────────────────────────────
  return (
    <div className="space-y-6 animate-fade-in">
      {showContact && participant && (
        <ContactCaptureSheet
          participantId={participant.id}
          onDone={handleContactDone}
          onSkip={() => {
            setShowContact(false)
            skipTimerRef.current = setTimeout(() => {
              if (!participant?.contact_captured) setShowContact(true)
            }, CONTACT_PROMPT_DELAY_MS)
          }}
        />
      )}

      {/* ── Active session banner ── */}
      <div className="bg-room-surface border border-room-border rounded-xl px-4 py-3 flex items-center gap-3">
        <div className="w-7 h-7 rounded-lg bg-room-accent/10 flex items-center justify-center flex-shrink-0">
          <Camera size={14} className="text-room-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-white truncate">
              {session.eventName ?? 'Personal shoot'}
            </span>
            <span className="px-1.5 py-px rounded bg-room-accent/15 text-room-accent text-[10px] font-semibold">
              {SHOOT_TYPE_LABELS[session.confirmedType]}
            </span>
          </div>
          {session.sessionBrief && (
            <p className="text-[11px] text-room-muted truncate mt-0.5">{session.sessionBrief}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-room-muted flex-shrink-0">
          <Clock size={11} />
          <span className="text-[11px]">{session.mediaCount} shot{session.mediaCount !== 1 ? 's' : ''}</span>
        </div>
        <button
          type="button"
          onClick={clearSession}
          className="text-[10px] text-room-muted hover:text-white transition-colors ml-1 flex-shrink-0"
        >
          End
        </button>
      </div>

      {/* Held status notice for anonymous users */}
      {participant?.is_anonymous && !participant?.contact_captured && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
          <p className="text-xs text-amber-300 font-semibold">Your shots are being held</p>
          <p className="text-[11px] text-amber-300/70 mt-0.5">
            Add your contact details to release them to the marketplace.
          </p>
          <button
            type="button"
            onClick={() => setShowContact(true)}
            className="mt-2 text-[11px] text-amber-300 font-semibold underline underline-offset-2"
          >
            Add contact now
          </button>
        </div>
      )}

      {/* Rights panel */}
      <div>
        <button
          type="button"
          onClick={() => setShowRights(v => !v)}
          className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors w-full"
        >
          <span className="text-xs font-semibold uppercase tracking-widest">Rights & distribution</span>
          <span className="ml-auto text-xs capitalize text-pink-400">{rights.usage_type}</span>
          {showRights ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showRights && (
          <div className="mt-3">
            <RightsPanel value={rights} onChange={setRights} />
          </div>
        )}
      </div>

      {/* ── Legal confirmations — must tick both before upload unlocks ── */}
      <div className="space-y-2">
        {[
          {
            id:      'holdsRights',
            checked: holdsRights,
            toggle:  () => setHoldsRights(v => !v),
            label:   'I hold all rights to this content',
          },
          {
            id:      'noMinors',
            checked: noMinors,
            toggle:  () => setNoMinors(v => !v),
            label:   'This content does not feature anyone under 18',
          },
        ].map(({ id, checked, toggle, label }) => (
          <button
            key={id}
            type="button"
            onClick={toggle}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all text-left"
            style={{ borderColor: checked ? '#d946ef55' : '#2a1040', background: checked ? '#d946ef0a' : '#0f0018' }}
          >
            <div className={cn(
              'w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all',
              checked ? 'bg-room-accent border-room-accent' : 'border-white/30 bg-transparent'
            )}>
              {checked
                ? <CheckSquare size={14} className="text-white" />
                : <span className="w-3 h-3 rounded-sm border border-white/30" />
              }
            </div>
            {/* White text — readable in dark environments */}
            <span className="text-sm text-white font-medium leading-snug">{label}</span>
          </button>
        ))}
      </div>

      <UploadZone
        disabled={!holdsRights || !noMinors}
        onFiles={(files) => {
        files.forEach(f => enqueue(
          f,
          participant?.id,
          session?.eventId ?? undefined
        ))
      }} />

      {activeItems.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white">Active</h3>
            {activeCount() > 0 && <Badge variant="accent">{activeCount()}</Badge>}
            {errorCount() > 0 && <Badge variant="error">{errorCount()} failed</Badge>}
          </div>
          {activeItems.map((item: UploadQueueItem) => (
            <QueueItemCard key={item.id} item={item} onRetry={retry} onDismiss={dismiss} onCancel={cancel} />
          ))}
        </div>
      )}

      {doneItems.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-room-muted">Completed</h3>
              <Badge variant="success">{doneItems.length}</Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 size={12} />}
              onClick={() => doneItems.forEach((i: UploadQueueItem) => dismiss(i.id))}
            >
              Clear
            </Button>
          </div>
          {doneItems.map((item: UploadQueueItem) => (
            <QueueItemCard key={item.id} item={item} onRetry={retry} onDismiss={dismiss} onCancel={cancel} />
          ))}
        </div>
      )}
    </div>
  )
}
